/**
 * Adds a white background to an image to fit a specific aspect ratio
 * @param {File} file - The image file to process
 * @param {number} targetAspect - The target aspect ratio (width/height)
 * @param {number} maxSize - Maximum size for the output image
 * @returns {Promise<File>} - Promise that resolves to the processed image file
 */
function addWhiteBackgroundToImage(file, targetAspect = 1, maxSize = 1800) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const sourceWidth = img.width;
      const sourceHeight = img.height;
      const sourceAspect = sourceWidth / sourceHeight;

      let canvasWidth;
      let canvasHeight;
      let imageX = 0;
      let imageY = 0;
      let imageWidth = sourceWidth;
      let imageHeight = sourceHeight;

      // Calculate canvas dimensions based on target aspect ratio
      if (sourceAspect > targetAspect) {
        // Image is wider than target aspect ratio
        canvasWidth = sourceWidth;
        canvasHeight = sourceWidth / targetAspect;
        imageY = (canvasHeight - sourceHeight) / 2;
      } else {
        // Image is taller than target aspect ratio
        canvasHeight = sourceHeight;
        canvasWidth = sourceHeight * targetAspect;
        imageX = (canvasWidth - sourceWidth) / 2;
      }

      // Scale down if the canvas is too large
      if (canvasWidth > maxSize || canvasHeight > maxSize) {
        const scale = Math.min(maxSize / canvasWidth, maxSize / canvasHeight);
        canvasWidth *= scale;
        canvasHeight *= scale;
        imageWidth *= scale;
        imageHeight *= scale;
        imageX *= scale;
        imageY *= scale;
      }

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');

      // Fill with white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Draw the image centered on the white background
      ctx.drawImage(img, imageX, imageY, imageWidth, imageHeight);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a new File object with the same name and type
            const processedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(processedFile);
          } else {
            reject(new Error('Failed to create Blob from canvas.'));
          }
        },
        file.type || 'image/png'
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load the image.'));
    };

    img.src = URL.createObjectURL(file);
  });
}

export default addWhiteBackgroundToImage;
